<script setup lang="ts">
import {
  BadgeComponent,
  type BadgeVariant,
  BoxComponent,
  DataTable,
  defaultFormatter,
  formatterProviderKey,
  PaginationComponent,
  TypographyComponent,
} from '@metapic-apps/shared'
import { computed, inject, ref, watch } from 'vue'

import { useGetOrders } from '@/api/orders.api'
import { useAdvertiserPermissions } from '@/lib/hooks/advertiser-permissions.hooks'
import type { OrderListFilter } from '@/lib/types/orders.types'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useGlobalFilterStore } from '@/stores/global-filter.stores'
import { LoadingComponent } from '@/ui-library'

const advertiserStore = useAdvertiserStore()
const filterStore = useGlobalFilterStore()
const { hideUsers, hideCosts } = useAdvertiserPermissions()
const { formatMoney: formatMoneyForContext, formatDateTimeLong } = inject(formatterProviderKey, defaultFormatter)
const shopId = computed<number | undefined>(() => advertiserStore.advertiser?.id)
const currency = computed<string>(() => advertiserStore.advertiser?.currencyIsoCode ?? '')
const page = ref(1)
const filter = computed<OrderListFilter>(() => ({
  page: page.value,
  dateFrom: filterStore.dateRange?.startDate,
  dateTo: filterStore.dateRange?.endDate,
}))
watch(
  () => [filterStore.dateRange?.startDate, filterStore.dateRange?.endDate],
  () => {
    page.value = 1
  },
)
const { data: orders, totalPages, isLoading } = useGetOrders(shopId, filter)
const formatMoney = (amount: number): string =>
  currency.value ? formatMoneyForContext({ amount, currency: currency.value }, { allowZero: true }) : '—'
const canLinkCreator = computed<boolean>(() => !hideUsers.value && !hideCosts.value)
const statusVariants: Record<string, BadgeVariant> = { paid: 'success', accepted: 'info', denied: 'danger' }
const statusLabel = (status: string): string => `${status.charAt(0).toUpperCase()}${status.slice(1)}`
const statusVariant = (status: string): BadgeVariant => statusVariants[status?.toLowerCase()] ?? 'neutral'
</script>

<template>
  <div class="orders-list">
    <DataTable>
      <template #header>
        <th scope="col" class="orders-list__col orders-list__col--date">Date</th>
        <th scope="col" class="orders-list__col orders-list__col--id">Order ID</th>
        <th scope="col" class="orders-list__col orders-list__col--creator">Creator</th>
        <th scope="col" class="orders-list__col orders-list__col--value">Order value</th>
        <th scope="col" class="orders-list__col orders-list__col--updated">Last updated</th>
        <th scope="col" class="orders-list__col orders-list__col--status">Status</th>
      </template>
      <tr v-if="isLoading">
        <td colspan="6" class="orders-list__state data-table__loading" aria-label="Loading"><LoadingComponent /></td>
      </tr>
      <tr v-else-if="orders.length === 0">
        <td colspan="6" class="orders-list__state"><TypographyComponent variant="title-medium">No orders found</TypographyComponent></td>
      </tr>
      <tr v-for="(order, index) in orders" v-else :key="`${order.id}-${index}`" class="orders-list__row">
        <td class="orders-list__col orders-list__col--date">
          <TypographyComponent variant="title-large" :title="formatDateTimeLong(order.orderAt)">{{
            formatDateTimeLong(order.orderAt)
          }}</TypographyComponent>
        </td>
        <td class="orders-list__col orders-list__col--id">
          <TypographyComponent variant="body-medium" :title="order.id">{{ order.id }}</TypographyComponent>
        </td>
        <td class="orders-list__col orders-list__col--creator">
          <RouterLink
            v-if="canLinkCreator && order.userId"
            :to="{ name: 'creator', params: { id: order.userId } }"
            :title="order.username"
            class="orders-list__creator-link"
            >{{ order.username }}</RouterLink
          >
          <TypographyComponent v-else variant="body-medium" :title="order.username">{{ order.username }}</TypographyComponent>
        </td>
        <td class="orders-list__col orders-list__col--value">
          <TypographyComponent variant="body-medium" :title="formatMoney(order.orderValue)">{{
            formatMoney(order.orderValue)
          }}</TypographyComponent>
        </td>
        <td class="orders-list__col orders-list__col--updated">
          <TypographyComponent variant="body-medium" :title="formatDateTimeLong(order.updatedAt)">{{
            formatDateTimeLong(order.updatedAt)
          }}</TypographyComponent>
        </td>
        <td class="orders-list__col orders-list__col--status">
          <BadgeComponent class="orders-list__badge" :variant="statusVariant(order.status)" :title="statusLabel(order.status)">{{
            statusLabel(order.status)
          }}</BadgeComponent>
        </td>
      </tr>
    </DataTable>
    <BoxComponent v-if="totalPages > 1" :mt="24"
      ><PaginationComponent v-model:current-page="page" :total-pages="totalPages"
    /></BoxComponent>
  </div>
</template>

<style scoped lang="scss">
.orders-list__col {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
th.orders-list__col {
  overflow: visible;
  text-overflow: clip;
  white-space: nowrap;
}
.orders-list__col--date {
  width: 200px;
}
.orders-list__col--id {
  width: auto;
}
.orders-list__col--creator {
  width: 160px;
}
.orders-list__col--value {
  width: 144px;

  text-align: right;
}
.orders-list__col--updated {
  width: 160px;
}
.orders-list__col--status {
  width: 136px;
  text-align: right;
}
.orders-list__creator-link {
  color: inherit;
  text-decoration: none;
}
.orders-list__creator-link:hover {
  text-decoration: underline;
}
.orders-list__state {
  color: rgba(0, 0, 0, 0.32);
  text-align: center;
}
</style>
